import { validateAnalysisState } from '@aibi/analysis-engine';
import type { SemanticModel } from '@aibi/contracts';
import { SemanticModelIndex } from '@aibi/semantic-model';
import { AppError, err, ok, type Result } from '@aibi/shared';
import {
  parseWorkspace,
  WORKSPACE_DOCUMENT_VERSION,
  type WorkspaceDocument,
} from '../workspace/model/workspace-document';
import type { SemanticModelCatalog } from '../workspace/services/semantic-model-catalog';
import type { ReportApi } from './report-api';

export interface OpenedReport {
  readonly model: SemanticModel;
  readonly document: WorkspaceDocument;
  readonly description: string;
  readonly updatedAt: string;
}

/**
 * Abre un reporte publicado: lo descarga, lo valida como documento de workspace, carga su
 * modelo y comprueba que cada visualizacion siga siendo coherente con el modelo actual.
 *
 * @example
 * const opened = await openReport({ reports, modelCatalog }, reportId);
 */
export async function openReport(
  services: { readonly reports: ReportApi; readonly modelCatalog: SemanticModelCatalog },
  reportId: string,
): Promise<Result<OpenedReport, AppError>> {
  const fetched = await services.reports.get(reportId);
  if (!fetched.ok) return fetched;
  const report = fetched.value;
  const parsed = parseWorkspace({
    version: WORKSPACE_DOCUMENT_VERSION,
    id: `report.${report.id}`,
    name: report.name,
    semanticModelId: report.semanticModelId,
    cards: report.cards,
  });
  if (!parsed.ok) {
    return err(new AppError('VALIDATION_ERROR', 'El reporte publicado no es valido.'));
  }

  // El catalogo combinado solo conoce los modelos del servidor despues de listarlos.
  const listed = await services.modelCatalog.list();
  if (!listed.ok) return listed;
  if (!listed.value.some((model) => model.id === report.semanticModelId)) {
    return err(
      new AppError(
        'NOT_FOUND',
        'El modelo de datos de este reporte ya no esta publicado. Pida a quien lo comparte que lo vuelva a publicar.',
      ),
    );
  }
  const loaded = await services.modelCatalog.load(report.semanticModelId);
  if (!loaded.ok) return loaded;
  const index = new SemanticModelIndex(loaded.value);
  if (!parsed.value.cards.every((card) => validateAnalysisState(card, index).ok)) {
    return err(
      new AppError(
        'CONFLICT',
        'El modelo de datos cambio y este reporte ya no coincide con el. Pida a quien lo comparte que lo vuelva a publicar.',
      ),
    );
  }
  return ok({
    model: loaded.value,
    document: parsed.value,
    description: report.description,
    updatedAt: report.updatedAt,
  });
}
